import * as echarts from 'echarts'
import React from 'react'
import { ChartArea, chartValueLabelStyle, classToFComponent, LifesHubColors } from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'
import { JSX } from 'react/jsx-runtime'

import { EChartViewClass } from '../../../the-consumer-market/views/helpers/base-echart'
import AccessCard, { AccessCardHeader, AccessEmptyState } from './aa_card'
import { AccessRankingScope } from '../aa_scopes'
import { formatNumber } from 'src/utils/formatter-utils'
import { formatNumberToCompactShortForm } from '../../../the-consumer-market/views/helpers/tcm_view_utils'

const INK_SECONDARY = LifesHubColors.muted
const AXIS_LINE = LifesHubColors.border
const GRID_LINE = LifesHubColors.gridLine

const OTHER_COLOR = LifesHubColors.borderStrong

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        alignSelf: 'stretch',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        minHeight: 200
    }
})

type ChartTopPagesProps = {
    className?: string
    style?: React.CSSProperties
    scope: AccessRankingScope
}

class ChartTopPagesViewClass extends EChartViewClass<ChartTopPagesProps> {
    // :: Fields

    scope!: AccessRankingScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState(props: ChartTopPagesProps) {
        this.scope = props.scope
        this.classes = useStyles().classes
        this.chartData = buildChartData(this.scope)
        this.loaded = this.scope.loaded
        super.onSyncState(props)
    }

    override render({ className, style, scope }: ChartTopPagesProps): React.JSX.Element {
        return (
            <AccessCard elevation={0} className={className} style={style}>
                <AccessCardHeader title={scope.caption || 'Relatórios mais acessados'} />
                {scope.loaded && scope.rows.length === 0 ? (
                    <AccessEmptyState
                        title="Nenhum relatório acessado"
                        text="Nenhum relatório foi aberto no período e nos filtros selecionados."
                    />
                ) : (
                    <ChartArea>{super.render({ className: this.classes.view, scope })}</ChartArea>
                )}
            </AccessCard>
        )
    }
}

export const AccessChartTopPagesView = classToFComponent(ChartTopPagesViewClass, React)
export default AccessChartTopPagesView

function buildChartData(scope: AccessRankingScope): echarts.EChartsOption {
    const rows = [...scope.rows].reverse()

    return {
        grid: { left: 8, right: 44, top: 8, bottom: 4, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            valueFormatter: (value) => formatNumber(Number(value))
        },
        xAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: GRID_LINE } },
            axisLabel: { color: INK_SECONDARY, formatter: (value: number) => formatNumberToCompactShortForm(value) }
        },
        yAxis: {
            type: 'category',
            data: rows.map((row) => row.label),
            axisTick: { show: false },
            axisLine: { lineStyle: { color: AXIS_LINE } },
            axisLabel: { color: INK_SECONDARY, width: 150, overflow: 'truncate' }
        },
        series: [
            {
                name: 'Acessos',
                type: 'bar',
                barMaxWidth: 18,
                data: rows.map((row) => ({
                    value: row.accesses,
                    itemStyle: {
                        color: row.isOther ? OTHER_COLOR : LifesHubColors.chartBase,
                        borderRadius: [0, 4, 4, 0]
                    }
                })),
                label: {
                    show: true,
                    position: 'right',
                    color: chartValueLabelStyle.color,
                    formatter: (params) => formatNumberToCompactShortForm(Number(params.value))
                }
            }
        ]
    }
}
